import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot } from "@fortawesome/pro-solid-svg-icons/faLocationDot";
import { faArrowLeft } from "@fortawesome/pro-solid-svg-icons/faArrowLeft";
import { faXmark } from "@fortawesome/pro-solid-svg-icons/faXmark";
import { faBell } from "@fortawesome/pro-regular-svg-icons/faBell";
import { Button, Input, cn } from "@wondermaps/ui";
import type { Ref } from "react";
import { useTranslation } from "react-i18next";
import type { HeadlineSponsor } from "./sponsor-types";
import type { VisitorNotification } from "./notification-types";
import { useNotificationsUnread } from "../../hooks/visitor/useNotificationsUnread";
import { ShareButton } from "./ShareButton";

interface SearchHeaderProps {
  isSearchMode: boolean;
  /** @deprecated unused after plan 111 PR3; kept for callsite compat until cleanup PR */
  onSearchOpen?: () => void;
  onSearchClose: () => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  inputRef?: Ref<HTMLInputElement>;
  logoUrl?: string;
  appName?: string;
  isEmbed?: boolean;
  headlineSponsor?: HeadlineSponsor | null;
  /** Notifications + share — renders bell + share buttons in the right-side floating island */
  notifications?: VisitorNotification[];
  onBellClick?: () => void;
  isTrayOpen?: boolean;
  mapSlug?: string;
  shareUrl?: string;
  shareTitle?: string;
  shareText?: string;
  brandAccent?: string | null;
}

export function SearchHeader({
  isSearchMode,
  onSearchClose,
  searchQuery,
  onSearchChange,
  inputRef,
  logoUrl,
  appName,
  isEmbed,
  headlineSponsor,
  notifications,
  onBellClick,
  isTrayOpen,
  mapSlug,
  shareUrl,
  shareTitle,
  shareText,
  brandAccent,
}: SearchHeaderProps) {
  const { t } = useTranslation("map");
  const unreadCount = useNotificationsUnread(notifications ?? [], mapSlug ?? "");
  const showActionsIsland = !isEmbed && !!onBellClick && !!mapSlug;
  return (
    <header
      className={cn(
        "relative flex h-14 items-center px-4",
        // Solid bg only in search mode, transparent + pass-through otherwise
        isSearchMode
          ? "bg-background border-b"
          : "bg-transparent pointer-events-none",
      )}
    >
      {/* Logo - fades out and slides left when entering search mode, hidden in embed */}
      {!isEmbed && (
        <div
          className={cn(
            "flex h-10 items-center gap-2 px-3 rounded-full bg-background/95 backdrop-blur-sm shadow-md transition-all duration-200 ease-out pointer-events-auto",
            isSearchMode && "opacity-0 -translate-x-4 !pointer-events-none",
          )}
        >
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={appName || t("common:logoAlt")}
              className="max-h-8 max-w-24 w-auto mix-blend-multiply dark:mix-blend-screen"
            />
          ) : (
            <>
              <FontAwesomeIcon
                icon={faLocationDot}
                className="h-6 w-6 text-primary"
              />
              <span className="font-semibold">{appName || "Wondermaps"}</span>
            </>
          )}
          {headlineSponsor && (
            <>
              <div className="h-5 w-px bg-gray-300 dark:bg-gray-600" />
              {headlineSponsor.websiteUrl ? (
                <a
                  href={headlineSponsor.websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={headlineSponsor.logoUrl}
                    alt={headlineSponsor.name}
                    className="max-h-6 max-w-20 w-auto object-contain mix-blend-multiply dark:mix-blend-screen"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                </a>
              ) : (
                <img
                  src={headlineSponsor.logoUrl}
                  alt={headlineSponsor.name}
                  className="max-h-6 max-w-20 w-auto object-contain mix-blend-multiply dark:mix-blend-screen"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              )}
            </>
          )}
        </div>
      )}

      {/* Back arrow - fades in from left when entering search mode */}
      <div
        className={cn(
          "absolute left-4 transition-all duration-200 ease-out",
          isSearchMode
            ? "opacity-100 translate-x-0"
            : "opacity-0 -translate-x-4 pointer-events-none",
        )}
      >
        <Button
          variant="ghost"
          size="icon"
          onClick={onSearchClose}
          aria-label={t("search.close")}
        >
          <FontAwesomeIcon icon={faArrowLeft} className="h-5 w-5" />
        </Button>
      </div>

      {/* Search input - expands when entering search mode */}
      <div
        className={cn(
          "absolute left-14 right-14 transition-all duration-200 ease-out",
          isSearchMode
            ? "opacity-100 scale-x-100"
            : "opacity-0 scale-x-0 origin-right pointer-events-none",
        )}
      >
        <div className="relative">
          <Input
            ref={inputRef}
            type="text"
            placeholder={t("search.placeholder")}
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pr-8 w-full"
            aria-label={t("search.ariaLabel")}
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-0 top-0 h-full"
              onClick={() => onSearchChange("")}
              aria-label={t("search.clear")}
            >
              <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Spacer to push icons to the right */}
      <div className="flex-1" />

      {/* Floating island: bell + share. Fades out when search mode activates. */}
      {showActionsIsland && (
        <div
          className={cn(
            "flex items-center rounded-full bg-background/95 backdrop-blur-sm shadow-md transition-all duration-200 ease-out pointer-events-auto",
            isSearchMode && "opacity-0 scale-0 !pointer-events-none",
          )}
        >
          <ShareButton
            url={shareUrl}
            title={shareTitle}
            text={shareText}
            brandAccent={brandAccent}
            className="rounded-full w-11 h-11"
          />
          <button
            type="button"
            onClick={onBellClick}
            aria-expanded={isTrayOpen}
            aria-controls="notifications-tray"
            aria-haspopup="dialog"
            className="relative w-11 h-11 flex items-center justify-center rounded-full hover:bg-muted transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={t("nav.notifications")}
          >
            <FontAwesomeIcon
              icon={faBell}
              className="h-4 w-4 text-foreground"
              style={brandAccent ? { color: brandAccent } : undefined}
              aria-hidden
            />
            {unreadCount > 0 && (
              <span
                className="absolute top-2 right-2 w-2 h-2 rounded-full bg-red-500 border border-background"
                aria-hidden
              />
            )}
          </button>
        </div>
      )}
    </header>
  );
}
